import React, { useEffect, useState } from 'react';
import { InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridEditInputCell, GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Tooltip } from '@eposnow/ui-core';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';

type Props = {
  gridRenderParams: GridRenderEditCellParams;
  labelKey?: string;
  autoComplete?: string;
  placeholder?: string;
  onValueChange?: (event: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>;
  min?: number;
  step?: number;
  max?: number;
  suffix?: string;
  prefix?: string;
  validationSchema?: any;
  type?: string;
};

const GridCellEdit = (props: Props) => {
  const {
    labelKey,
    gridRenderParams,
    autoComplete = 'on',
    onValueChange = () => {
      /* void */
    },
    min = 0,
    step = 1,
    max = 0,
    suffix = '',
    prefix = '',
    validationSchema,
    type = '',
  } = props;

  const { id: cellId, field } = gridRenderParams;
  const { t } = useTranslation();
  const theme = useTheme();
  const [tooltipMessage, setTooltipMessage] = useState('');

  // Validate with the schema and set the tooltip status and message if needed
  const validateAndManageTooltip = (newValue) => {
    const validateValue = async (value) => {
      const formattedValue = value === '' && type === 'number' ? null : value;

      if (!validationSchema) return;

      await validationSchema
        .validate({ [field]: formattedValue })
        .then(() => {
          setTooltipMessage('');
        })
        .catch((err) => {
          setTooltipMessage(t(`${err.errors[0]}`, { field: t(labelKey) }));
        });
    };

    validateValue(newValue);
  };

  useEffect(() => {
    validateAndManageTooltip(gridRenderParams.value);
  }, [gridRenderParams.value]);

  return (
    <Tooltip title={tooltipMessage} placement="bottom" theme={theme}>
      <Box sx={{ width: '100%' }}>
        <GridEditInputCell
          label={t(labelKey)}
          id={cellId}
          type={type}
          fullWidth
          autoComplete={autoComplete}
          size="small"
          inputProps={
            type === 'number'
              ? {
                  min,
                  step,
                  max,
                  style: { ...(prefix && { paddingLeft: 0 }), width: '100%' },
                }
              : {}
          }
          startAdornment={
            prefix && (
              <InputAdornment position="start" sx={{ paddingLeft: '16px' }}>
                <span>{prefix}</span>
              </InputAdornment>
            )
          }
          endAdornment={
            suffix && (
              <InputAdornment position="end" sx={{ paddingRight: '16px' }}>
                <span>{suffix}</span>
              </InputAdornment>
            )
          }
          sx={{
            textAlign: 'end',
          }}
          {...gridRenderParams}
        />
      </Box>
    </Tooltip>
  );
};

export default GridCellEdit;
